export const SECTION_REORDER_CLICK_THRESHOLD_PX = 6;
export const SECTION_REORDER_ENTER_MS = 300;

export type SectionDropLine = { id: string; edge: "before" | "after" };

/**
 * Move `ids[src]` so it lands at the slot described by `insertBefore`
 * (index in the pre-remove list: 0 = before first, length = after last).
 * Returns null when the drop is a no-op.
 */
export function applySectionReorder(
  ids: string[],
  src: number,
  insertBefore: number,
): string[] | null {
  if (src < 0 || src >= ids.length) return null;
  if (insertBefore === src || insertBefore === src + 1) return null;

  let at = insertBefore;
  if (src < insertBefore) at = insertBefore - 1;
  at = Math.max(0, Math.min(at, ids.length - 1));

  const next = [...ids];
  const [item] = next.splice(src, 1);
  if (!item) return null;
  next.splice(at, 0, item);

  if (next.every((id, i) => id === ids[i])) return null;
  return next;
}

/**
 * Resolve insertion index from pointer position over elements marked with
 * `dataAttr` (value = index into `ids`).
 */
export function resolveSectionInsertBefore(
  clientX: number,
  clientY: number,
  ids: readonly string[],
  dataAttr: string,
): { insertBefore: number; line: SectionDropLine | null } {
  const el = document.elementFromPoint(clientX, clientY);
  const rowEl = el?.closest(`[${dataAttr}]`) as HTMLElement | null;

  const fromRow = (
    row: HTMLElement,
    idx: number,
    y: number,
  ): { insertBefore: number; line: SectionDropLine | null } => {
    const rect = row.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const edge: "before" | "after" = y < mid ? "before" : "after";
    const insertBefore = edge === "before" ? idx : idx + 1;
    const lineId = ids[idx];
    return { insertBefore, line: lineId ? { id: lineId, edge } : null };
  };

  if (rowEl) {
    const idx = parseInt(rowEl.getAttribute(dataAttr) ?? "-1", 10);
    if (idx >= 0 && idx < ids.length) return fromRow(rowEl, idx, clientY);
  }

  const rows = document.querySelectorAll<HTMLElement>(`[${dataAttr}]`);
  if (rows.length === 0) return { insertBefore: ids.length, line: null };

  let bestRow: HTMLElement | null = null;
  let bestIdx = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const row of rows) {
    const idx = parseInt(row.getAttribute(dataAttr) ?? "-1", 10);
    if (idx < 0) continue;
    const rect = row.getBoundingClientRect();
    let dist = 0;
    if (clientY < rect.top) dist = rect.top - clientY;
    else if (clientY > rect.bottom) dist = clientY - rect.bottom;
    if (dist < bestDist) {
      bestDist = dist;
      bestRow = row;
      bestIdx = idx;
    }
  }

  if (!bestRow || bestIdx < 0) return { insertBefore: ids.length, line: null };
  return fromRow(bestRow, bestIdx, clientY);
}
